import { useEffect, useCallback } from 'react';

interface ShortcutConfig {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
  handler: () => void;
  description?: string;
  enabled?: boolean;
}

export const useKeyboardShortcuts = (shortcuts: ShortcutConfig[]) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in input fields
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true'
    ) {
      return;
    }

    shortcuts.forEach((shortcut) => {
      if (shortcut.enabled === false) return;

      const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
      const ctrlMatch = !shortcut.ctrlKey || event.ctrlKey === shortcut.ctrlKey;
      const shiftMatch = !shortcut.shiftKey || event.shiftKey === shortcut.shiftKey;
      const altMatch = !shortcut.altKey || event.altKey === shortcut.altKey;
      const metaMatch = !shortcut.metaKey || event.metaKey === shortcut.metaKey;

      if (keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch) {
        event.preventDefault();
        shortcut.handler();
      }
    });
  }, [shortcuts]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return shortcuts.map(({ key, ctrlKey, shiftKey, altKey, metaKey, description }) => ({
    key,
    ctrlKey,
    shiftKey,
    altKey,
    metaKey,
    description,
    displayKey: [
      metaKey && '⌘',
      ctrlKey && 'Ctrl',
      altKey && 'Alt',
      shiftKey && 'Shift',
      key.toUpperCase(),
    ].filter(Boolean).join('+'),
  }));
};

export default useKeyboardShortcuts;